import React, { useState } from 'react';
import { IDoctor, ITreatment } from '../types/clinic.types';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import CloseIcon from '@mui/icons-material/Close';
interface Props {
  doctor: IDoctor | null; // null for 'Add', object for 'Edit'
  allTreatments: ITreatment[];
  onClose: () => void;
  onSave: (doctor: IDoctor) => void;
}

const DEFAULT_DOCTOR: Omit<IDoctor, 'id'> = {
  firstName: '',
  lastName: '',
  studies: [],
  speciality: '',
  languages: [],
  assignedTreatmentIds: [],
  image: 'https://via.placeholder.com/150.png?text=New+Doctor',
};

export const DoctorFormModal: React.FC<Props> = ({ doctor, allTreatments, onClose, onSave }) => {
  const [formData, setFormData] = useState(doctor || DEFAULT_DOCTOR);
  const isEditMode = !!doctor;

  const treatmentRowMatches = (storedId: string, t: ITreatment) =>
    String(storedId) === String(t.id) ||
    (t.treatmentId != null && String(storedId) === String(t.treatmentId));

  const handleTreatmentToggle = (t: ITreatment) => {
    const rowId = String(t.id);
    const currentIds = formData.assignedTreatmentIds;
    const has = currentIds.some((id) => treatmentRowMatches(String(id), t));
    const newIds = has
      ? currentIds.filter((id) => !treatmentRowMatches(String(id), t))
      : [...currentIds, rowId];
    setFormData((prev) => ({ ...prev, assignedTreatmentIds: newIds }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData as IDoctor);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1000] p-4">
      <div className="bg-white rounded-[8px] shadow-2xl max-w-[500px] w-full p-3 relative max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold text-slate-800 mb-0">{isEditMode ? 'Edit Doctor' : 'Add New Doctor'}</h3>
            <button type="button" className="p-1.5 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-full transition-colors" onClick={onClose}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </button>
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Assign Treatments</label>
              <div className="max-h-[120px] overflow-y-auto border border-slate-200 p-2.5 rounded-lg">
                {allTreatments.length === 0 && <p className="text-xs text-slate-500">No treatments available to assign.</p>}
                {allTreatments.map(t => (
                  <div className="flex items-center gap-1.5 mb-1.5" key={t.id}>
                    <input 
                      type="checkbox" 
                      id={`t-${t.id}`}
                      checked={formData.assignedTreatmentIds.some((id) =>
                        treatmentRowMatches(String(id), t)
                      )}
                      onChange={() => handleTreatmentToggle(t)}
                      className="w-3.5 h-3.5 text-primary border-slate-300 rounded focus:ring-primary"
                    />
                    <label htmlFor={`t-${t.id}`} className="text-xs font-normal text-slate-700 cursor-pointer">{t.name}</label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button type="button" className="px-4 py-2.5 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-colors text-xs" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="flex items-center gap-1.5 px-4 py-2.5 bg-gradient-to-r from-primary to-primaryLight text-white rounded-lg font-medium hover:shadow-lg hover:shadow-primary/20 transition-all text-xs">
              <SaveAltIcon sx={{ fontSize: 16 }} />
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
